import type { PriceHistory, ProductPriceItem } from './types';

/** UDOMI-991: Old MSRP is available when previous published MSRP exists and is positive. */
export function hasPreviousMsrp(priceHistory?: PriceHistory): boolean {
  return priceHistory?.previousMsrp != null && priceHistory.previousMsrp > 0;
}

/** UDOMI-991: % Change is shown only when Old MSRP exists and change percentage is calculable. */
export function shouldShowPercentChange(priceHistory?: PriceHistory): boolean {
  if (!priceHistory || !hasPreviousMsrp(priceHistory)) {
    return false;
  }
  return priceHistory.msrpChangePercentage != null;
}

/** Format signed percent change for export and display (e.g. +2.50%). */
export function formatPctChange(pct: number): string {
  return `${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%`;
}

/** UDOMI-389: Average MSRP % change for products in a category that have price history. */
export function categoryAverageChange(items: ProductPriceItem[]): number | undefined {
  const changes = items
    .map(i => i.priceHistory?.msrpChangePercentage)
    .filter((v): v is number => v !== undefined && v !== null);
  if (changes.length === 0) {
    return undefined;
  }
  return changes.reduce((sum, v) => sum + v, 0) / changes.length;
}

/** Removes the trailing dealer tier from a price sheet category (e.g. "Standard Gable T1" → "Standard Gable"). */
export function stripTierFromCategory(category: string, tier?: string): string {
  if (!tier) return category;
  const suffix = ` ${tier}`;
  return category.endsWith(suffix) ? category.slice(0, -suffix.length) : category;
}

/**
 * Product Size Price Totals shows MSRP and costs, which do not depend on the dealer tier.
 * The price sheet API returns one row per tier, so keep the first tier's rows and drop the tier from the category.
 */
export function sizeTotalsProducts(products: ProductPriceItem[]): ProductPriceItem[] {
  const firstTier = products.find(p => p.tier)?.tier;
  if (!firstTier) return products;
  return products
    .filter(p => !p.tier || p.tier === firstTier)
    .map(p => ({ ...p, category: stripTierFromCategory(p.category, p.tier) }));
}
